import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  filterComposeRecipientSuggestions,
  formatComposeRecipientToken,
  type ComposeRecipientSuggestion,
  type FilteredComposeRecipientSuggestions,
} from '../utils/composeRecipientSuggestions';
import { extractRecipientEmail } from '../utils/recipientUtils';

type RecipientField = 'to' | 'cc' | 'bcc';

const EMPTY_FILTERED: FilteredComposeRecipientSuggestions = {
  recent: [],
  addressBook: [],
  flat: [],
};

export function bareEmailsFromTokens(tokens: string[]): string[] {
  return tokens
    .map((token) => extractRecipientEmail(token) ?? token.trim())
    .filter(Boolean);
}

export function useRecipientSuggestionUi(
  recipientSuggestions: ComposeRecipientSuggestion[],
  excludeTokens: string[],
) {
  const listId = useId();
  const [activeField, setActiveField] = useState<RecipientField | null>(null);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [queryByField, setQueryByField] = useState<Partial<Record<RecipientField, string>>>({});

  const excludeEmails = useMemo(() => bareEmailsFromTokens(excludeTokens), [excludeTokens]);

  const activeQuery = activeField ? queryByField[activeField] ?? '' : '';

  const filtered = useMemo(() => {
    if (!open || !activeField) return EMPTY_FILTERED;
    return filterComposeRecipientSuggestions(recipientSuggestions, activeQuery, excludeEmails);
  }, [open, activeField, recipientSuggestions, activeQuery, excludeEmails]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [activeQuery, activeField, open]);

  useEffect(() => {
    if (filtered.flat.length === 0) return;
    if (highlightedIndex > filtered.flat.length - 1) {
      setHighlightedIndex(filtered.flat.length - 1);
    }
  }, [filtered.flat.length, highlightedIndex]);

  const openForField = useCallback((field: RecipientField, query: string) => {
    setActiveField(field);
    setQueryByField((prev) => ({ ...prev, [field]: query }));
    setOpen(true);
    setHighlightedIndex(0);
  }, []);

  const syncQuery = useCallback((field: RecipientField, query: string) => {
    setQueryByField((prev) => ({ ...prev, [field]: query }));
    setActiveField(field);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const moveHighlight = useCallback(
    (delta: number) => {
      if (filtered.flat.length === 0) return;
      setOpen(true);
      setHighlightedIndex((prev) => {
        const next = prev + delta;
        if (next < 0) return filtered.flat.length - 1;
        if (next >= filtered.flat.length) return 0;
        return next;
      });
    },
    [filtered.flat.length],
  );

  const highlightedItem =
    open && filtered.flat.length > 0 ? filtered.flat[highlightedIndex] ?? null : null;

  const formatSelection = useCallback((item: ComposeRecipientSuggestion) => {
    return formatComposeRecipientToken(item.name, item.email);
  }, []);

  const showList = open && Boolean(activeField) && filtered.flat.length > 0;

  return useMemo(
    () => ({
      listId,
      activeField,
      open: showList,
      filtered,
      highlightedIndex,
      highlightedItem,
      setHighlightedIndex,
      openForField,
      syncQuery,
      close,
      moveHighlight,
      formatSelection,
    }),
    [
      listId,
      activeField,
      showList,
      filtered,
      highlightedIndex,
      highlightedItem,
      openForField,
      syncQuery,
      close,
      moveHighlight,
      formatSelection,
    ],
  );
}
